/* global _LTracker */ // @flow
import axios from 'axios';
import { IChildInfo, IOnlineWaiverState } from './OnlineWaiver';
import * as React from 'react';
import { Translate  } from "react-localize-redux";
import { toast } from 'react-toastify';

const apiUrl = process.env.REACT_APP_API_URL;

const MAX_ATTEMPTS = 5;

export type Dictionary<T> = {
    [key: string]: T;
};

export const XHRError = Object.freeze({
    Error: 0,
    Timeout: 1,
    Abort: 2
});

/* This is for retry/retries */
async function attemptLoop(url, requestType, object={}) {
    let attempts = 0;
    let lastError;
    while(++attempts <= MAX_ATTEMPTS) {
        let response;
        try {
            if(requestType === "get") response = await axios.get(url);
            else if(requestType === "post") response = await axios.post(url, object);

            // console.log(response);
            if(response !== undefined && response !== null && response.status < 400) {
                return {
                    success: true,
                    result: response.data
                }
            }
            // console.log(response.data);
        } catch(err) {
            console.log(`Error attempting ${requestType}@${url}`);
            console.log("Attempt: ", attempts);
            console.error({error: err, info: `[${attempts}]Error attempting ${requestType}@${url}`});

            lastError = err;

            // If the app gets a 500 response at any point, it really should be resetting as this means there was a server error while
            // trying to parse the data. If the specific function could be returned to instead of resetting the entire app state
            // that would be ideal (maybe to refresh the ReCAPTCHA token for example)
            _LTracker.push({
                lastError,
                errResponse: err.response,
                info: {
                    type: `API Failure`,
                    details: `Attempt [${attempts}] failed for ${requestType}@${url} with response ${err.response ? err.response.status : undefined}`
                }
            });
            if (err.response !== undefined && err.response.status === 500) {
                console.error(`Failed to perform request due to server issue. Flow cancelled.`);
                break;
            }
        }
    }

    _LTracker.push({ lastError, info: {
        type: `API Failure`,
        details: `Maximum number of errors attempting ${requestType}@${url}`
    }});
    toast.error(<Translate id="online_waiver_js.failure_fetching_data"/>, {
        autoClose: false,
        position:  toast.POSITION.BOTTOM_CENTER
     });

    throw lastError;
}

export interface ILocationData {
    locationId: number;
    displayName: string;
}

/* Gets the list of locations that are available to book */
export async function getLocations(): Promise<Dictionary<ILocationData>> {
    const endpointUrl = '/api/corp/getWaiverLocations';
    try {
        const result = await attemptLoop(apiUrl + endpointUrl, 'get');

        if (!result.success || result.result === undefined || result.result === null) return 0;

        return result.result;
    } catch (err) {
        console.error(err);
        throw err;
    }
}

/* Gets the list of all countries, provinces / states and cities for dropdown lists (in North America)*/
export async function getRegions() {
    const endpointUrl = '/api/corp/getWaiverRegions';
    try {
        const result = await attemptLoop(apiUrl + endpointUrl, 'get');

        if (!result.success || result.result === undefined || result.result === null) return 0;

        // console.log(result.result);
        return result.result;
    } catch (err) {
        console.error(err);
        throw err;
    }
}

export interface IHearAboutData {
    method: string;
}

/* Gets the list of locations that are available to book */
export async function getHearAboutData(location: number): Promise<IHearAboutData[]> {
    const endpointUrl = `/api/corp/getHearAbout/${location}`;
    try {
        const result = await attemptLoop(apiUrl + endpointUrl, 'get');

        if (!result.success || result.result === undefined || result.result === null) {
            console.log(result);
            return [];
        }

        return result.result;
    } catch (err) {
        console.error(err);
        throw err;
    }
}

export interface IWaiverData {
    firstName: string;
    lastName: string;
    preferredName: string;
    email: string;
    phone: string;
    gender: string;
    birthDate: string;
    address: string;
    city: string;
    provinceState: string;
    country: string;
    postalZip: string;
    childInfos: IChildInfo[];
    location: number;
    hearAbout: string[];
    otherEntry: string;
    agreedMediaRelease: boolean;
    requestNewsletter: boolean;
    sms_opt_in: boolean;
    waiverVersion: string;
    languageCode: string;
    covidFree: boolean;
}

export async function submitWaiverData(state: IOnlineWaiverState): Promise<boolean> {
    const endpointUrl = '/api/customer/submitWaiverData/';

    const childInfos = []
    state.childInfos.forEach(function(value) {
        // Skip any children who don't have proper data and snuck through validation
        if (value.firstName !== '' && value.lastName !== '' && value.gender !== '') {
            value.dateOfBirth = (value.firstName !== '') ? value.dateOfBirth.format("YYYY-MM-DD") : '';
            childInfos.push(value);
        }
    })

    const waiverData: IWaiverData = {
        firstName: state.firstName,
        lastName: state.lastName,
        preferredName: state.preferredName,
        email: state.email,
        phone: state.phone,
        gender: state.gender,
        birthDate: state.dateOfBirth.format("YYYY-MM-DD"),
        address: state.address,
        city: state.city,
        provinceState: state.provinceState,
        country: state.country,
        postalZip: state.postalZipCode,
        childInfos: state.signForChildren === 'Myself' ? [] : childInfos,
        location: state.location,
        hearAbout: state.hearAbout,
        otherEntry: state.otherEntry,
        agreedMediaRelease: state.checkedRelease,
        requestNewsletter: state.checkedNewsletter,
        sms_opt_in: state.checkedSmsOptIn,
        waiverVersion: state.version,
        languageCode: state.languageCode,
        covidFree: state.checkedCovid,
        recaptcha: state.recaptcha,
        recaptcha_version: `Waiver|${state.version}`
    };

    try {
        const result = await attemptLoop(apiUrl + endpointUrl, 'post', waiverData);
        if (result.sucess === false || result.result === undefined) {
            return undefined;
        }
        console.log(result);
        console.log('Data sent! Response received!');
        return result.result;
    } catch (err) {
        console.error(`Submit Waiver Data Failed with the error Number: `, err);
        throw err;
    }

}
